import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { wyscoutMatchesAPI, PlayerEventAnalysis as PlayerEventAnalysisData, MatchFilterOption } from '@/services/api';
import SearchableMultiSelect from './PlayerEventAnalysis/SearchableMultiSelect';
import PassingSection from './PlayerEventAnalysis/PassingSection';
import ShootingSection from './PlayerEventAnalysis/ShootingSection';
import DefensiveSection from './PlayerEventAnalysis/DefensiveSection';
import ActivityByPeriodSection from './PlayerEventAnalysis/ActivityByPeriodSection';
import LoadingStatus from '@/components/ui/LoadingStatus';
import DelayedLoading from '@/components/ui/DelayedLoading';
import OperationStatus from '@/components/ui/OperationStatus';

interface PlayerEventAnalysisProps {
  playerId: string;
  wyscoutPlayerId: number;
}

const PlayerEventAnalysis: React.FC<PlayerEventAnalysisProps> = ({ playerId, wyscoutPlayerId }) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<PlayerEventAnalysisData | null>(null);
  const [filterOptions, setFilterOptions] = useState<MatchFilterOption[]>([]);
  const [selectedMatchIds, setSelectedMatchIds] = useState<Set<string>>(new Set());
  const [selectedPositions, setSelectedPositions] = useState<Set<string>>(new Set());
  const [selectedVenues, setSelectedVenues] = useState<Set<string>>(new Set());
  const initialLoad = useRef(true);
  const requestGeneration = useRef(0);
  const lastFilters = useRef<{ matchIds?: number[]; positions?: string[]; venues?: string[] } | undefined>(undefined);

  const fetchData = useCallback(async (filters?: { matchIds?: number[]; positions?: string[]; venues?: string[] }) => {
    const generation = ++requestGeneration.current;
    lastFilters.current = filters;
    try {
      setLoading(true);
      setError(null);
      const response = await wyscoutMatchesAPI.getPlayerEventAnalysis(playerId, filters);
      if (generation !== requestGeneration.current) return;
      setData(response.data);
      // Only set filter options on initial load
      if (initialLoad.current && response.filterOptions) {
        setFilterOptions(response.filterOptions);
        initialLoad.current = false;
      }
    } catch (err: any) {
      if (generation !== requestGeneration.current) return;
      setError(err?.response?.data?.error || err?.message || 'Failed to load event analysis');
    } finally {
      if (generation === requestGeneration.current) setLoading(false);
    }
  }, [playerId]);

  // Initial fetch
  useEffect(() => {
    if (!wyscoutPlayerId) return;
    initialLoad.current = true;
    requestGeneration.current += 1;
    setData(null);
    setError(null);
    setSelectedMatchIds(new Set());
    setSelectedPositions(new Set());
    setSelectedVenues(new Set());
    void fetchData();
    return () => { requestGeneration.current += 1; };
  }, [playerId, wyscoutPlayerId, fetchData]);

  // Refetch on filter change (skip initial)
  useEffect(() => {
    if (initialLoad.current || !wyscoutPlayerId) return;
    const filters: { matchIds?: number[]; positions?: string[]; venues?: string[] } = {};
    if (selectedMatchIds.size > 0) filters.matchIds = Array.from(selectedMatchIds).map(Number);
    if (selectedPositions.size > 0) filters.positions = Array.from(selectedPositions);
    if (selectedVenues.size > 0) filters.venues = Array.from(selectedVenues);
    fetchData(Object.keys(filters).length > 0 ? filters : undefined);
  }, [selectedMatchIds, selectedPositions, selectedVenues, wyscoutPlayerId, fetchData]);

  // Derive unique positions and venues from filterOptions
  const positionOptions = useMemo(() => {
    const unique = Array.from(new Set(filterOptions.map(o => o.position).filter(Boolean)));
    return unique.sort().map(p => ({ value: p, label: p }));
  }, [filterOptions]);

  const venueOptions = useMemo(() => {
    const unique = Array.from(new Set(filterOptions.map(o => o.venue).filter(Boolean)));
    return unique.sort().map(v => ({ value: v, label: v.charAt(0).toUpperCase() + v.slice(1) }));
  }, [filterOptions]);

  const matchOptions = useMemo(() => {
    return filterOptions.map(o => ({
      value: String(o.matchId),
      label: `${o.label}${o.dateUtc ? ` (${o.dateUtc.slice(0, 10)})` : ''}`,
    }));
  }, [filterOptions]);

  const hasActiveFilters = selectedMatchIds.size > 0 || selectedPositions.size > 0 || selectedVenues.size > 0;

  const resetFilters = () => {
    setSelectedMatchIds(new Set());
    setSelectedPositions(new Set());
    setSelectedVenues(new Set());
  };

  if (loading && !data) {
    return (
      <div className="mt-6 bg-white dark:bg-gray-900 rounded-lg shadow p-6">
        <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Event Analysis</h4>
        <DelayedLoading loading>
          <LoadingStatus label="Loading player event analysis" className="h-16 justify-center" />
        </DelayedLoading>
      </div>
    );
  }

  if (error && !data) {
    return (
      <div className="mt-6 bg-white dark:bg-gray-900 rounded-lg shadow p-6">
        <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-4">Event Analysis</h4>
        <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  if (!data || data.totalEvents === 0) {
    return null;
  }

  const { passing, shooting, defensive, activityByPeriod } = data;

  // Build pass zone map data
  const passZoneMax = Math.max(...Object.values(passing.zones).map(z => z.count), 1);
  const passZones: Record<string, { count: number; label: string; color: string; opacity: number }> = {};
  for (const [zone, stats] of Object.entries(passing.zones)) {
    passZones[zone] = {
      count: stats.count,
      label: `${stats.accuracyPct.toFixed(0)}%`,
      color: '#3b82f6',
      opacity: Math.max(0.15, (stats.count / passZoneMax) * 0.85),
    };
  }

  // Build shot zone map data
  const shotZoneMax = Math.max(...Object.values(shooting.zones).map(z => z.shots), 1);
  const shotZones: Record<string, { count: number; label: string; color: string; opacity: number }> = {};
  for (const [zone, stats] of Object.entries(shooting.zones)) {
    const hasGoal = stats.goals > 0;
    shotZones[zone] = {
      count: stats.shots,
      label: stats.goals > 0 ? `${stats.goals}G` : `${stats.xg.toFixed(1)}xG`,
      color: hasGoal ? '#22c55e' : '#f97316',
      opacity: Math.max(0.15, (stats.shots / shotZoneMax) * 0.85),
    };
  }

  const directionEntries: Array<{ key: string; label: string }> = [
    { key: 'forward', label: 'Forward' },
    { key: 'lateral', label: 'Lateral' },
    { key: 'backward', label: 'Backward' },
  ];

  // Build defensive zone map data
  const defZones = defensive.zones || {};
  const defZoneMax = Math.max(...Object.values(defZones).map(z => z.total), 1);
  const defZoneMap: Record<string, { count: number; label: string; color: string; opacity: number }> = {};
  for (const [zone, stats] of Object.entries(defZones)) {
    const hasWonDuels = stats.duelsWon > 0;
    defZoneMap[zone] = {
      count: stats.total,
      label: (() => {
        if (stats.duels > 0) return `${stats.duelsWon}/${stats.duels}D`;
        if (stats.interceptions > 0) return `${stats.interceptions}I`;
        if (stats.recoveries > 0) return `${stats.recoveries}R`;
        return '';
      })(),
      color: hasWonDuels ? '#ef4444' : '#f97316',
      opacity: Math.max(0.15, (stats.total / defZoneMax) * 0.85),
    };
  }

  const periods = Object.entries(activityByPeriod).sort(([a], [b]) => a.localeCompare(b));
  const maxPeriodTotal = Math.max(...periods.map(([, p]) => p.total), 1);

  return (
    <div className="mt-6 bg-white dark:bg-gray-900 rounded-lg shadow p-6">
      <h4 className="text-md font-semibold text-gray-900 dark:text-gray-100 mb-1">Event Analysis</h4>
      <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
        {data.matchCount} match{data.matchCount !== 1 ? 'es' : ''} &middot; {data.totalEvents} events
      </p>

      {loading && (
        <LoadingStatus variant="inline" label="Refreshing player event analysis" className="mb-3" />
      )}
      {error && (
        <div className="mb-4">
          <OperationStatus
            state="error"
            label="Player event analysis refresh failed"
            description={`${error}. Cached analysis remains visible.`}
            onRetry={() => { void fetchData(lastFilters.current); }}
          />
        </div>
      )}

      {/* Filter Bar */}
      {filterOptions.length > 1 && (
        <div className="flex flex-wrap items-center gap-2 mb-4 pb-3 border-b border-gray-200 dark:border-gray-700">
          <SearchableMultiSelect
            label="Match"
            options={matchOptions}
            selected={selectedMatchIds}
            onChange={setSelectedMatchIds}
            searchable
          />
          {positionOptions.length > 1 && (
            <SearchableMultiSelect
              label="Position"
              options={positionOptions}
              selected={selectedPositions}
              onChange={setSelectedPositions}
            />
          )}
          {venueOptions.length > 1 && (
            <SearchableMultiSelect
              label="Home/Away"
              options={venueOptions}
              selected={selectedVenues}
              onChange={setSelectedVenues}
            />
          )}
          {hasActiveFilters && (
            <button
              onClick={resetFilters}
              className="px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors"
            >
              Reset Filters
            </button>
          )}
        </div>
      )}

      {/* Passing Analysis */}
      <PassingSection passing={passing} passZones={passZones} directionEntries={directionEntries} />

      {/* Shooting Analysis */}
      {shooting.total > 0 && (
        <ShootingSection shooting={shooting} shotZones={shotZones} />
      )}

      {/* Defensive Analysis */}
      <DefensiveSection defensive={defensive} defZoneMap={defZoneMap} />

      {/* Activity by Period */}
      {periods.length > 0 && (
        <ActivityByPeriodSection periods={periods} maxPeriodTotal={maxPeriodTotal} />
      )}
    </div>
  );
};

export default PlayerEventAnalysis;
